// Wind-field footprint geometry. ATCF wind radii are 4 per-quadrant values
// [NE, SE, SW, NW] in nautical miles (max extent of that wind threshold in each
// quadrant). We treat them as defined at the quadrant centrelines (45/135/225/315°)
// and interpolate around the circle to draw a smooth filled polygon.

const CENTERS = [45, 135, 225, 315];

function radiusAt(bearing: number, radii: number[]): number {
  const b = ((bearing % 360) + 360) % 360;
  for (let i = 0; i < 4; i++) {
    const c0 = CENTERS[i];
    let c1 = CENTERS[(i + 1) % 4];
    if (c1 < c0) c1 += 360;
    let bb = b;
    if (bb < c0) bb += 360;
    if (bb >= c0 && bb <= c1) {
      const t = (bb - c0) / (c1 - c0);
      return radii[i] + (radii[(i + 1) % 4] - radii[i]) * t;
    }
  }
  return radii[0];
}

/** Returns a closed [lon,lat] ring for the wind threshold, or null if no extent. */
export function windRadiiPolygon(
  lat: number,
  lon: number,
  radiiNm: number[] | null,
): [number, number][] | null {
  if (!radiiNm || radiiNm.length < 4 || !radiiNm.some((r) => r > 0)) return null;
  const cosLat = Math.cos((lat * Math.PI) / 180) || 1e-6;
  const ring: [number, number][] = [];
  for (let b = 0; b < 360; b += 10) {
    const r = Math.max(0, radiusAt(b, radiiNm));
    const degR = r / 60; // nm → degrees latitude
    const br = (b * Math.PI) / 180;
    const dlat = degR * Math.cos(br);
    const dlon = (degR * Math.sin(br)) / cosLat;
    ring.push([lon + dlon, lat + dlat]);
  }
  return ring;
}

/** [lat,lon] a given distance (nm) and bearing (deg) from a point (flat approx). */
function pointAt(lat: number, lon: number, bearingDeg: number, distNm: number): [number, number] {
  const cosLat = Math.cos((lat * Math.PI) / 180) || 1e-6;
  const d = distNm / 60;
  const b = (bearingDeg * Math.PI) / 180;
  return [lat + d * Math.cos(b), lon + (d * Math.sin(b)) / cosLat];
}

/** Pie-slice sector [lon,lat] ring (tornado right-front quadrant). */
export function sectorPolygon(
  lat: number,
  lon: number,
  radiusNm: number,
  startBrg: number,
  endBrg: number,
  steps = 20,
): [number, number][] {
  const ring: [number, number][] = [[lon, lat]];
  for (let i = 0; i <= steps; i++) {
    const brg = startBrg + ((endBrg - startBrg) * i) / steps;
    const [la, lo] = pointAt(lat, lon, brg, radiusNm);
    ring.push([lo, la]);
  }
  ring.push([lon, lat]);
  return ring;
}

// (Surge is now drawn as a continuous coastline ribbon in Canvas.tsx from the
// dense coastline + per-frame tiers; the old per-segment gradient-band geometry
// has been removed.)
